import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Minus, Users, ChevronDown, ChevronUp, BarChart2,
  Headphones, Mic, BookOpen, PenTool, Target, FileText, X, Save, Printer, Brain } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY_CONFIG = {
  listening: { icon: Headphones, color: '#002FA7', label: 'Listening' },
  speaking: { icon: Mic, color: '#3b82f6', label: 'Speaking' },
  reading: { icon: BookOpen, color: '#22c55e', label: 'Reading' },
  writing: { icon: PenTool, color: '#f59e0b', label: 'Writing' },
};

// ========== GOAL SETTING MODAL ==========
const GoalModal = ({ student, currentScores, existingGoals, onClose, onSave }) => {
  const [goals, setGoals] = useState({
    listening: existingGoals?.listening || '',
    speaking: existingGoals?.speaking || '',
    reading: existingGoals?.reading || '',
    writing: existingGoals?.writing || '',
  });
  const [targetDate, setTargetDate] = useState(existingGoals?.target_date || '');
  const [notes, setNotes] = useState(existingGoals?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const goalsData = {};
    for (const mod of ["listening", "speaking", "reading", "writing"]) {
      if (goals[mod] !== '') goalsData[mod] = parseFloat(goals[mod]);
    }
    if (Object.keys(goalsData).length === 0) {
      toast.error("Set at least one modality goal");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/goals`, {
        student_id: student.student_id,
        goals: goalsData,
        target_date: targetDate || null,
        notes,
      });
      toast.success(`Goals set for ${student.name}`);
      onSave(student.student_id, goalsData, targetDate, notes);
      onClose();
    } catch {
      toast.error("Failed to save goals");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg border-2 border-gray-200 shadow-xl" onClick={e => e.stopPropagation()} data-testid="goal-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
              <Target size={20} className="text-[#002FA7]" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Set WIDA Goals</h3>
              <p className="text-sm text-gray-500">{student.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-500">Set target proficiency levels (1.0–6.0) for each modality. Leave blank to skip.</p>
          <div className="grid grid-cols-2 gap-3">
            {["listening", "speaking", "reading", "writing"].map(mod => {
              const cfg = MODALITY_CONFIG[mod];
              const Icon = cfg.icon;
              const current = currentScores?.[mod]?.latest || 0;
              return (
                <div key={mod} className="border p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} style={{ color: cfg.color }} />
                    <span className="text-sm font-semibold">{cfg.label}</span>
                    <span className="text-xs text-gray-400 ml-auto">Current: {current || '—'}</span>
                  </div>
                  <input
                    type="number"
                    min="1" max="6" step="0.5"
                    value={goals[mod]}
                    onChange={e => setGoals(prev => ({ ...prev, [mod]: e.target.value }))}
                    placeholder="Target"
                    className="w-full border px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
                    data-testid={`goal-input-${mod}`}
                  />
                </div>
              );
            })}
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Target Date</label>
            <input
              type="date"
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
              className="w-full border px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="goal-target-date"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Notes (IEP, conference, etc.)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g., IEP Spring review target"
              className="w-full border px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7] resize-none"
              data-testid="goal-notes"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-[#002FA7] hover:bg-[#001f6e]" data-testid="goal-save-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save Goals'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ========== GOAL PROGRESS BAR ==========
const GoalProgress = ({ current, goal, color }) => {
  if (!goal || !current) return null;
  const pct = Math.min(100, Math.max(0, (current / goal) * 100));
  const isAchieved = current >= goal;
  return (
    <div className="flex items-center gap-1" title={`Current: ${current} / Goal: ${goal}`}>
      <div className="w-14 h-2 bg-gray-200 overflow-hidden rounded-full">
        <div
          className={`h-full rounded-full transition-all ${isAchieved ? 'bg-green-500' : ''}`}
          style={{ width: `${pct}%`, backgroundColor: isAchieved ? undefined : color }}
        />
      </div>
      <span className={`text-[9px] font-bold ${isAchieved ? 'text-green-600' : 'text-gray-400'}`}>
        {isAchieved ? '\u2713' : `${Math.round(pct)}%`}
      </span>
    </div>
  );
};


const GrowthTracker = ({ classId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [studentGrowth, setStudentGrowth] = useState({});
  const [classGoals, setClassGoals] = useState({});
  const [goalModalStudent, setGoalModalStudent] = useState(null);

  useEffect(() => {
    if (classId) {
      fetchClassGrowth();
      fetchClassGoals();
    }
  }, [classId]);

  const fetchClassGrowth = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/growth/class/${classId}`);
      setData(res.data);
    } catch { toast.error("Failed to load growth data"); }
    finally { setLoading(false); }
  };

  const fetchClassGoals = async () => {
    try {
      const res = await axios.get(`${API}/goals/class/${classId}`);
      setClassGoals(res.data.goals || {});
    } catch { /* no goals yet */ }
  };

  const fetchStudentGrowth = async (studentId) => {
    if (studentGrowth[studentId]) {
      setExpandedStudent(expandedStudent === studentId ? null : studentId);
      return;
    }
    try {
      const res = await axios.get(`${API}/growth/student/${studentId}`);
      setStudentGrowth(prev => ({ ...prev, [studentId]: res.data }));
      setExpandedStudent(studentId);
    } catch { toast.error("Failed to load student growth"); }
  };

  const handleGoalSave = (studentId, goals, targetDate, notes) => {
    setClassGoals(prev => ({ ...prev, [studentId]: { goals, target_date: targetDate, notes } }));
  };

  const openGoalModal = (student, e) => {
    e.stopPropagation();
    setGoalModalStudent(student);
  };

  const exportReport = async (student, e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API}/reports/student-growth/${student.student_id}`);
      const w = window.open('', '_blank');
      w.document.write(generateReportHTML(res.data));
      w.document.close();
      setTimeout(() => { w.print(); }, 500);
    } catch {
      toast.error("Failed to generate report");
    }
  };

  const GrowthIndicator = ({ value, size = "md" }) => {
    if (value === null || value === undefined) return <span className="text-gray-400">&mdash;</span>;
    const sizeClass = size === "sm" ? "text-xs" : "text-sm";
    if (value > 0) return <span className={`${sizeClass} font-bold text-green-700 flex items-center gap-1`}><TrendingUp size={size === "sm" ? 12 : 14} /> +{value}</span>;
    if (value < 0) return <span className={`${sizeClass} font-bold text-red-600 flex items-center gap-1`}><TrendingDown size={size === "sm" ? 12 : 14} /> {value}</span>;
    return <span className={`${sizeClass} font-medium text-gray-500 flex items-center gap-1`}><Minus size={size === "sm" ? 12 : 14} /> 0.0</span>;
  };

  const ProficiencyBar = ({ score, maxScore = 6.0 }) => {
    const pct = Math.min(100, (score / maxScore) * 100);
    const colors = [
      { min: 0, max: 1.9, color: "bg-red-500" },
      { min: 2.0, max: 2.9, color: "bg-orange-500" },
      { min: 3.0, max: 3.9, color: "bg-yellow-500" },
      { min: 4.0, max: 4.9, color: "bg-blue-500" },
      { min: 5.0, max: 5.9, color: "bg-green-500" },
      { min: 6.0, max: 6.0, color: "bg-emerald-600" },
    ];
    const barColor = colors.find(c => score >= c.min && score <= c.max)?.color || "bg-gray-400";
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 h-3 bg-gray-200 overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-mono font-bold w-8">{score}</span>
      </div>
    );
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading growth data...</div>;
  if (!data) return <div className="py-8 text-center text-gray-500">No growth data available</div>;

  const { students, class_growth, windows } = data;

  return (
    <div data-testid="growth-tracker">
      {/* Goal Modal */}
      {goalModalStudent && (
        <GoalModal
          student={goalModalStudent}
          currentScores={goalModalStudent.modality_growth}
          existingGoals={classGoals[goalModalStudent.student_id] ? {
            ...classGoals[goalModalStudent.student_id].goals,
            target_date: classGoals[goalModalStudent.student_id].target_date,
            notes: classGoals[goalModalStudent.student_id].notes,
          } : null}
          onClose={() => setGoalModalStudent(null)}
          onSave={handleGoalSave}
        />
      )}

      {/* Class-level summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="border-2 border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-[#002FA7]">{class_growth.students_assessed}</div>
          <div className="text-xs text-gray-500">Students Assessed</div>
        </div>
        <div className="border-2 border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold">{class_growth.students_multi_assessed}</div>
          <div className="text-xs text-gray-500">Multiple Assessments</div>
        </div>
        <div className="border-2 border-gray-200 p-4 text-center">
          <div className={`text-2xl font-bold ${class_growth.avg_growth > 0 ? 'text-green-700' : class_growth.avg_growth < 0 ? 'text-red-600' : 'text-gray-700'}`}>
            {class_growth.avg_growth > 0 ? '+' : ''}{class_growth.avg_growth}
          </div>
          <div className="text-xs text-gray-500">Avg Growth</div>
        </div>
        <div className="border-2 border-green-200 bg-green-50 p-4 text-center">
          <div className="text-2xl font-bold text-green-700">{class_growth.students_improved}</div>
          <div className="text-xs text-gray-500">Improved</div>
        </div>
        <div className="border-2 border-red-200 bg-red-50 p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{class_growth.students_declined}</div>
          <div className="text-xs text-gray-500">Declined</div>
        </div>
      </div>

      {/* Per-Modality Class Summary */}
      {class_growth.modality_stats && (
        <div className="mb-6" data-testid="class-modality-stats">
          <h4 className="font-semibold mb-2 text-sm text-gray-600">Modality Performance</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {["listening", "speaking", "reading", "writing"].map(mod => {
              const stats = class_growth.modality_stats[mod] || {};
              const cfg = MODALITY_CONFIG[mod];
              const Icon = cfg.icon;
              return (
                <div key={mod} className="border-2 border-gray-200 p-4" data-testid={`class-modality-${mod}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} style={{ color: cfg.color }} />
                    <span className="text-sm font-semibold">{cfg.label}</span>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: cfg.color }}>
                    {stats.avg_score || '\u2014'}
                  </div>
                  <div className="text-xs text-gray-500 mb-1">Avg Score</div>
                  <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-gray-100">
                    <span className={`font-semibold ${stats.avg_growth > 0 ? 'text-green-700' : stats.avg_growth < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {stats.avg_growth > 0 ? '+' : ''}{stats.avg_growth || 0} avg growth
                    </span>
                  </div>
                  <div className="flex gap-2 text-xs text-gray-400 mt-1">
                    <span className="text-green-600">{stats.students_improved || 0} up</span>
                    <span className="text-red-500">{stats.students_declined || 0} down</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Assessment Windows Timeline */}
      {windows.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold mb-2 text-sm text-gray-600">Assessment Windows</h4>
          <div className="flex gap-2 flex-wrap">
            {windows.map(w => (
              <span key={w.id} className="text-xs border px-3 py-1 bg-gray-50 font-medium">
                {w.name} ({w.exam_count || 0} exams)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Student Growth Table */}
      <div className="border-2 border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#002FA7] text-white">
              <th className="text-left p-3 font-semibold">Student</th>
              <th className="text-center p-3 font-semibold">Assessments</th>
              <th className="text-center p-3 font-semibold">Latest</th>
              <th className="text-center p-3 font-semibold">Growth</th>
              <th className="text-center p-3 font-semibold">L / S / R / W</th>
              <th className="text-center p-3 font-semibold">Goal Progress</th>
              <th className="text-center p-3 font-semibold w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, idx) => {
              const sGoals = classGoals[s.student_id]?.goals || {};
              return (
                <React.Fragment key={s.student_id}>
                  <tr
                    className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/30 cursor-pointer`}
                    onClick={() => fetchStudentGrowth(s.student_id)}
                    data-testid={`growth-row-${s.student_id}`}
                  >
                    <td className="p-3 font-medium">{s.name}</td>
                    <td className="p-3 text-center">{s.assessments}</td>
                    <td className="p-3 text-center"><ProficiencyBar score={s.latest_score} /></td>
                    <td className="p-3 text-center"><GrowthIndicator value={s.growth} /></td>
                    <td className="p-3 text-center">
                      {s.modality_growth ? (
                        <div className="flex items-center justify-center gap-1">
                          {["listening", "speaking", "reading", "writing"].map(mod => {
                            const mg = s.modality_growth[mod];
                            if (!mg) return <span key={mod} className="text-xs text-gray-300">&mdash;</span>;
                            return (
                              <span key={mod} className={`text-xs font-bold px-1 py-0.5 ${
                                mg.growth > 0 ? 'text-green-700 bg-green-50' :
                                mg.growth < 0 ? 'text-red-600 bg-red-50' :
                                'text-gray-500 bg-gray-50'
                              }`} title={`${MODALITY_CONFIG[mod].label}: ${mg.latest} (${mg.growth > 0 ? '+' : ''}${mg.growth})`}>
                                {mg.latest}
                              </span>
                            );
                          })}
                        </div>
                      ) : <span className="text-xs text-gray-300">&mdash;</span>}
                    </td>
                    <td className="p-3 text-center">
                      {Object.keys(sGoals).length > 0 && s.modality_growth ? (
                        <div className="flex items-center justify-center gap-1">
                          {["listening", "speaking", "reading", "writing"].map(mod => {
                            const goal = sGoals[mod];
                            const current = s.modality_growth[mod]?.latest || 0;
                            return goal ? (
                              <GoalProgress key={mod} current={current} goal={goal} color={MODALITY_CONFIG[mod].color} />
                            ) : <span key={mod} className="w-14" />;
                          })}
                        </div>
                      ) : <span className="text-xs text-gray-300">No goals</span>}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={e => openGoalModal(s, e)}
                          className="p-1.5 hover:bg-blue-50 text-[#002FA7] transition-colors"
                          title="Set Goals"
                          data-testid={`set-goal-${s.student_id}`}
                        >
                          <Target size={14} />
                        </button>
                        <a
                          href={`/growth-trajectory/${s.student_id}`}
                          onClick={e => e.stopPropagation()}
                          className="p-1.5 hover:bg-indigo-50 text-indigo-600 transition-colors"
                          title="Dimensional Growth Trajectory"
                          data-testid={`trajectory-btn-${s.student_id}`}
                        >
                          <Brain size={14} />
                        </a>
                        <a
                          href={`/parent-report/${s.student_id}`}
                          onClick={e => e.stopPropagation()}
                          className="p-1.5 hover:bg-green-50 text-green-600 transition-colors"
                          title="Parent Report"
                          data-testid={`parent-report-btn-${s.student_id}`}
                        >
                          <FileText size={14} />
                        </a>
                        <button
                          onClick={e => exportReport(s, e)}
                          className="p-1.5 hover:bg-blue-50 text-[#002FA7] transition-colors"
                          title="Export Growth Report (PDF)"
                          data-testid={`export-report-${s.student_id}`}
                        >
                          <Printer size={14} />
                        </button>
                        {expandedStudent === s.student_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </td>
                  </tr>
                  {expandedStudent === s.student_id && studentGrowth[s.student_id] && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <StudentGrowthDetail
                          data={studentGrowth[s.student_id]}
                          goals={sGoals}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {students.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-gray-500">No students with results yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const MiniSparkline = ({ points, color, width = 120, height = 32, goal }) => {
  if (!points || points.length < 2) return null;
  const allVals = goal ? [...points, goal] : points;
  const min = Math.min(...allVals, 1);
  const max = Math.max(...allVals, 6);
  const range = max - min || 1;
  const pathPts = points.map((s, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = height - 2 - ((s - min) / range) * (height - 4);
    return `${x},${y}`;
  });
  const goalY = goal ? height - 2 - ((goal - min) / range) * (height - 4) : null;
  return (
    <svg width={width} height={height} className="inline-block">
      {goalY !== null && (
        <line x1="0" y1={goalY} x2={width} y2={goalY} stroke="#22c55e" strokeWidth="1" strokeDasharray="3,2" />
      )}
      <polyline points={pathPts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((s, i) => {
        const x = (i / (points.length - 1)) * width;
        const y = height - 2 - ((s - min) / range) * (height - 4);
        return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
      })}
    </svg>
  );
};

const StudentGrowthDetail = ({ data, goals }) => {
  const { data_points, growth } = data;
  const domains = ["listening", "speaking", "reading", "writing"];

  const modalitySeries = {};
  for (const mod of domains) {
    modalitySeries[mod] = data_points.map(dp => dp[`${mod}_wida`] || 0).filter(v => v > 0);
  }

  return (
    <div className="bg-gray-50 border-t-2 border-[#002FA7] p-5" data-testid="student-growth-detail">
      {/* Domain growth with sparklines + goals */}
      {growth && Object.keys(growth).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {domains.map(d => {
            const cfg = MODALITY_CONFIG[d];
            const Icon = cfg.icon;
            const goal = goals?.[d];
            const latestScore = modalitySeries[d].length > 0 ? modalitySeries[d][modalitySeries[d].length - 1] : 0;
            return (
              <div key={d} className="bg-white border p-3" data-testid={`growth-domain-${d}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={14} style={{ color: cfg.color }} />
                  <span className="text-xs text-gray-500 font-semibold">{cfg.label}</span>
                  {goal && (
                    <span className="text-[9px] ml-auto font-bold text-green-600" title={`Goal: ${goal}`}>
                      <Target size={10} className="inline mr-0.5" />{goal}
                    </span>
                  )}
                </div>
                <div className={`text-lg font-bold ${(growth[d] || 0) > 0 ? 'text-green-700' : (growth[d] || 0) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {(growth[d] || 0) > 0 ? '+' : ''}{growth[d] || 0}
                </div>
                {modalitySeries[d].length >= 2 && (
                  <MiniSparkline points={modalitySeries[d]} color={cfg.color} goal={goal} />
                )}
                {goal && latestScore > 0 && (
                  <GoalProgress current={latestScore} goal={goal} color={cfg.color} />
                )}
              </div>
            );
          })}
          <div className="bg-white border p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <BarChart2 size={14} className="text-gray-500" />
              <span className="text-xs text-gray-500 font-semibold">Overall</span>
            </div>
            <div className={`text-lg font-bold ${(growth.overall || 0) > 0 ? 'text-green-700' : (growth.overall || 0) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {(growth.overall || 0) > 0 ? '+' : ''}{growth.overall || 0}
            </div>
            {data_points.length >= 2 && (
              <MiniSparkline
                points={data_points.map(dp => dp.overall_score || 0).filter(v => v > 0)}
                color="#6b7280"
              />
            )}
          </div>
        </div>
      )}

      {/* Timeline of assessments */}
      <h5 className="font-semibold text-sm mb-2">Assessment History</h5>
      <div className="space-y-2">
        {data_points.map((dp, i) => (
          <div key={i} className="bg-white border p-3 flex items-center justify-between text-sm">
            <div>
              <span className="font-medium">{dp.window_name}</span>
              <span className="text-gray-500 ml-2">{dp.scored_at?.slice(0, 10)}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-gray-500">L: {dp.listening_wida}</span>
              <span className="text-xs text-gray-500">S: {dp.speaking_wida}</span>
              <span className="text-xs text-gray-500">R: {dp.reading_wida}</span>
              <span className="text-xs text-gray-500">W: {dp.writing_wida}</span>
              <span className="font-bold text-[#002FA7]">{dp.overall_score}</span>
              <span className={`text-xs px-2 py-0.5 font-semibold ${
                dp.wida_level <= 2 ? 'bg-red-100 text-red-800' :
                dp.wida_level <= 4 ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                L{dp.wida_level} {dp.wida_level_name}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ========== PDF REPORT GENERATOR ==========
function generateReportHTML(report) {
  const mods = ["listening", "speaking", "reading", "writing"];
  const modLabels = { listening: "Listening", speaking: "Speaking", reading: "Reading", writing: "Writing" };
  const modColors = { listening: '#002FA7', speaking: '#3b82f6', reading: '#22c55e', writing: '#f59e0b' };

  const goalRows = mods.map(mod => {
    const p = report.modalities[mod];
    const goalVal = p.goal ? `${p.goal}` : '\u2014';
    const progress = p.goal && p.latest ? `${Math.min(100, Math.round((p.latest / p.goal) * 100))}%` : '\u2014';
    return `<tr>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;font-weight:600;color:${modColors[mod]}">${modLabels[mod]}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${p.latest || '\u2014'}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${goalVal}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:600;color:${p.growth > 0 ? '#15803d' : p.growth < 0 ? '#dc2626' : '#6b7280'}">${p.growth > 0 ? '+' : ''}${p.growth}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${progress}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${p.level_name}</td>
    </tr>`;
  }).join('');

  const canDoRows = mods.map(mod => {
    const p = report.modalities[mod];
    return `<div style="margin-bottom:12px;padding:12px;border:1px solid #e5e7eb;border-left:4px solid ${modColors[mod]}">
      <div style="font-weight:700;color:${modColors[mod]};margin-bottom:4px">${modLabels[mod]} (Level ${p.level})</div>
      <div style="font-size:13px;color:#374151;margin-bottom:6px"><strong>Can Do:</strong> ${p.can_do || 'N/A'}</div>
      <div style="font-size:13px;color:#6b7280"><strong>Next Steps:</strong> ${p.next_steps || 'N/A'}</div>
    </div>`;
  }).join('');

  const historyRows = report.history.map(h => `<tr>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px">${h.date}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px;text-align:center">${h.grade}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px;text-align:center">${h.listening}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px;text-align:center">${h.speaking}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px;text-align:center">${h.reading}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px;text-align:center">${h.writing}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px;text-align:center;font-weight:700">${h.overall}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #f3f4f6;font-size:12px;text-align:center">${h.is_adaptive ? 'Adaptive' : 'Standard'}</td>
  </tr>`).join('');

  return `<!DOCTYPE html><html><head><title>MOSAIC Growth Report - ${report.student.name}</title>
<style>
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 24px; color: #1f2937; }
  table { width: 100%; border-collapse: collapse; }
  h1, h2, h3 { font-family: 'Outfit', sans-serif; }
</style></head><body>
<div style="border-bottom:3px solid #002FA7;padding-bottom:16px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:flex-end">
  <div>
    <h1 style="margin:0;color:#002FA7;font-size:28px">MOSAIC</h1>
    <div style="color:#6b7280;font-size:12px">Per-Modality Student Growth Report</div>
  </div>
  <div style="text-align:right;font-size:12px;color:#6b7280">
    Generated: ${new Date().toLocaleDateString()}<br/>
    K–12 Language Proficiency Assessment
  </div>
</div>

<div style="display:flex;gap:24px;margin-bottom:24px">
  <div style="flex:1;border:2px solid #e5e7eb;padding:16px">
    <h3 style="margin:0 0 8px 0;font-size:14px;color:#6b7280">Student Information</h3>
    <div style="font-size:18px;font-weight:700">${report.student.name}</div>
    <div style="font-size:13px;color:#6b7280">Grade: ${report.student.grade_level || 'N/A'} | Home Language: ${report.student.home_language || 'N/A'}</div>
  </div>
  <div style="flex:1;border:2px solid #002FA7;padding:16px;background:#f0f4ff">
    <h3 style="margin:0 0 8px 0;font-size:14px;color:#002FA7">Overall Composite</h3>
    <div style="font-size:32px;font-weight:900;color:#002FA7">${report.overall.latest}</div>
    <div style="font-size:13px;color:#6b7280">${report.overall.level_name} | ${report.overall.total_assessments} assessment(s)</div>
  </div>
</div>

<h2 style="font-size:16px;border-bottom:2px solid #e5e7eb;padding-bottom:8px;margin-bottom:16px">Per-Modality Performance & Goals</h2>
<table>
  <thead><tr style="background:#002FA7;color:white">
    <th style="padding:8px;text-align:left">Modality</th>
    <th style="padding:8px;text-align:center">Current</th>
    <th style="padding:8px;text-align:center">Goal</th>
    <th style="padding:8px;text-align:center">Growth</th>
    <th style="padding:8px;text-align:center">Progress</th>
    <th style="padding:8px;text-align:center">Level</th>
  </tr></thead>
  <tbody>${goalRows}</tbody>
</table>
${report.goal_target_date ? `<div style="margin-top:8px;font-size:12px;color:#6b7280">Target Date: ${report.goal_target_date}</div>` : ''}
${report.goal_notes ? `<div style="margin-top:4px;font-size:12px;color:#6b7280">Notes: ${report.goal_notes}</div>` : ''}

<h2 style="font-size:16px;border-bottom:2px solid #e5e7eb;padding-bottom:8px;margin:24px 0 16px">Can-Do Descriptors & Next Steps</h2>
${canDoRows}

<h2 style="font-size:16px;border-bottom:2px solid #e5e7eb;padding-bottom:8px;margin:24px 0 16px">Assessment History</h2>
<table>
  <thead><tr style="background:#f3f4f6">
    <th style="padding:6px 8px;text-align:left;font-size:12px">Date</th>
    <th style="padding:6px 8px;text-align:center;font-size:12px">Grade</th>
    <th style="padding:6px 8px;text-align:center;font-size:12px">L</th>
    <th style="padding:6px 8px;text-align:center;font-size:12px">S</th>
    <th style="padding:6px 8px;text-align:center;font-size:12px">R</th>
    <th style="padding:6px 8px;text-align:center;font-size:12px">W</th>
    <th style="padding:6px 8px;text-align:center;font-size:12px">Overall</th>
    <th style="padding:6px 8px;text-align:center;font-size:12px">Type</th>
  </tr></thead>
  <tbody>${historyRows}</tbody>
</table>

<div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;font-size:10px;color:#9ca3af">
  MOSAIC &mdash; Multilingual Oral &amp; Written Student Assessment for Instructional Clarity<br/>
  This report is generated for educational purposes. WIDA&reg; is a registered trademark of the Board of Regents of the University of Wisconsin System.
</div>
</body></html>`;
}


export default GrowthTracker;
